import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import {
  fetchDashboardStats,
  fetchDistrictUsers,
  fetchDistrictTaskSubmit,
  fetchMunicipalityAttendanceToday,
  fetchMunicipalityAttendanceDefaultersToday, // <-- Make sure this action exists
} from "../redux/actions/dashboardActions";
import {
  FiUsers,
  FiClipboard,
  FiCalendar,
  FiCheckCircle,
  FiMapPin,
} from "react-icons/fi";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
} from "recharts";
import theme from "../components/shared/theme";
import { dropdownStyle } from "../components/shared/themeStyles";
import { CSVLink } from "react-csv";

const COLORS = [
  theme.colors.primary,
  theme.colors.secondary,
  theme.colors.warning,
  theme.colors.danger,
  theme.colors.success,
  "#3B82F6",
  "#14B8A6",
  "#F43F5E",
  "#A78BFA",
  "#F87171",
  "#34D399",
];

const StatCard = ({ icon, label, value }) => (
  <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-gray-500 text-sm font-medium">{label}</p>
        <p className="text-2xl font-bold text-gray-800 mt-1">{value}</p>
      </div>
      <div className="p-3 rounded-full bg-gray-100">{icon}</div>
    </div>
  </div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;

    return (
      <div
        className="bg-white border rounded-lg shadow-lg max-w-sm text-sm"
        style={{
          maxHeight: "320px",
          overflowY: "auto",
          pointerEvents: "auto",
          padding: "1rem",
        }}
      >
        <h3 className="text-base font-semibold text-gray-800 mb-2">{label}</h3>
        <div className="mb-2 text-gray-700">
          <p>
            Total Users: <span className="font-medium">{data.total_users}</span>
          </p>
          <p>
            Total Attendance:{" "}
            <span className="font-medium">{data.total_attendance}</span>
          </p>
        </div>
        <div className="divide-y divide-gray-200">
          {data.tooltipInfo.map((info, idx) => (
            <div key={idx} className="py-2 space-y-1">
              <p className="font-medium text-gray-900">{info.name}</p>
              <p className="text-gray-600">🧾 {info.designation}</p>
              <p className="text-gray-600">
                ✅ Status: {info.attendance_status}
              </p>
              <p className="text-gray-600">
                🕒 In:{" "}
                {info.check_in
                  ? moment(info.check_in, "HH:mm:ss").format("h:mm A")
                  : "N/A"}
              </p>
              <p className="text-gray-600">
                ⏰ Out:{" "}
                {info.check_out
                  ? moment(info.check_out, "HH:mm:ss").format("h:mm A")
                  : "N/A"}
              </p>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return null;
};

const Dashboard = () => {
  const dispatch = useDispatch();
  const {
    stats,
    loading,
    districtUsers,
    districtTaskSubmit,
    municipalityAttendanceToday,
    municipalityAttendanceDefaultersToday,
  } = useSelector((state) => state.dashboard);

  // Attendance filters
  const [selectedDistrictAttendance, setSelectedDistrictAttendance] = useState("");
  const [selectedDateAttendance, setSelectedDateAttendance] = useState(new Date());

  // Defaulters filters
  const [selectedDistrictDefaulters, setSelectedDistrictDefaulters] = useState("");
  const [selectedDateDefaulters, setSelectedDateDefaulters] = useState(new Date());

  useEffect(() => {
    dispatch(fetchDashboardStats());
    dispatch(fetchDistrictUsers());
    dispatch(fetchDistrictTaskSubmit());
  }, [dispatch]);

  useEffect(() => {
    const formattedDate = moment(selectedDateAttendance).format("YYYY-MM-DD");
    dispatch(fetchMunicipalityAttendanceToday(formattedDate));
  }, [dispatch, selectedDateAttendance]);

  useEffect(() => {
    const formattedDate = moment(selectedDateDefaulters).format("YYYY-MM-DD");
    dispatch(fetchMunicipalityAttendanceDefaultersToday(formattedDate));
  }, [dispatch, selectedDateDefaulters]);

  // --- District lists for dropdowns ---
  // const allDistrictsAttendance = [
  //   ...new Set(municipalityAttendanceToday.map((item) => item.district)),
  // ];
  // --- District lists for dropdowns ---
const allDistrictsAttendance = [
  ...new Set(
    municipalityAttendanceToday
      .map((item) => item.district)
      .filter(Boolean) // remove null/undefined
  ),
];

  // const allDistrictsDefaulters = [
  //   ...new Set(municipalityAttendanceDefaultersToday.map((item) => item.district)),
  // ];
  // Merge attendance and defaulters districts for Defaulters dropdown
const allDistrictsDefaulters = [
  ...new Set(
    [
      ...municipalityAttendanceDefaultersToday.map((item) => item.district),
      ...municipalityAttendanceToday.map((item) => item.district),
    ].filter(Boolean)
  ),
];

  // Debug logs to verify dropdown data
  console.log("Attendance Districts:", allDistrictsAttendance);
  console.log("Defaulters Districts:", allDistrictsDefaulters);

  // Prepare filtered data for attendance
  const filteredMunicipalityData = selectedDistrictAttendance
    ? Object.values(
        municipalityAttendanceToday
          .filter((item) => item.district === selectedDistrictAttendance)
          .reduce((acc, curr) => {
            if (!acc[curr.municipality]) {
              acc[curr.municipality] = {
                municipality: curr.municipality,
                total_users: 0,
                total_attendance: 0,
                tooltipInfo: [],
              };
            }
            acc[curr.municipality].total_users += 1;
            if (curr.attendance_status === "present") {
              acc[curr.municipality].total_attendance += 1;
            }
            acc[curr.municipality].tooltipInfo.push({
              name: curr.name,
              designation: curr.designation,
              check_in: curr.check_in,
              check_out: curr.check_out,
              attendance_status: curr.attendance_status,
            });
            return acc;
          }, {})
      )
    : [];



  // const filteredMunicipalityDefaultersData =
  // selectedDistrictDefaulters
  //   ? Object.values(
  //       municipalityAttendanceDefaultersToday
  //         .filter((item) =>
  //           selectedDistrictDefaulters === "all"
  //             ? true
  //             : item.district === selectedDistrictDefaulters
  //         )
  //         .reduce((acc, curr) => {
  //           if (!acc[curr.municipality]) {
  //             acc[curr.municipality] = {
  //               municipality: curr.municipality,
  //               total_users: 0,
  //               total_attendance: 0,
  //               tooltipInfo: [],
  //             };
  //           }
  //           acc[curr.municipality].total_users += 1;
  //           if (curr.attendance_status === "absent") {
  //             acc[curr.municipality].total_attendance += 1;
  //           }
  //           acc[curr.municipality].tooltipInfo.push({
  //             name: curr.name,
  //             designation: curr.designation,
  //             check_in: curr.check_in,
  //             check_out: curr.check_out,
  //             attendance_status: curr.attendance_status,
  //           });
  //           return acc;
  //         }, {})
  //     )
  //   : [];

  // Grouped data for municipalities with district info
const filteredMunicipalityDefaultersData = selectedDistrictDefaulters
  ? Object.values(
      municipalityAttendanceDefaultersToday
        .filter((item) =>
          selectedDistrictDefaulters === "all"
            ? true
            : item.district === selectedDistrictDefaulters
        )
        .reduce((acc, curr) => {
          if (!acc[curr.municipality]) {
            acc[curr.municipality] = {
              district: curr.district, // ✅ store district here
              municipality: curr.municipality,
              total_users: 0,
              total_attendance: 0,
              tooltipInfo: [],
            };
          }
          acc[curr.municipality].total_users += 1;
          if (curr.attendance_status === "absent") {
            acc[curr.municipality].total_attendance += 1;
          }
          acc[curr.municipality].tooltipInfo.push({
            name: curr.name,
            designation: curr.designation,
            check_in: curr.check_in,
            check_out: curr.check_out,
            attendance_status: curr.attendance_status,
          });
          return acc;
        }, {})
    )
  : [];

  // CSV data for attendance
  const csvAttendanceData = filteredMunicipalityData.flatMap((item) =>
    item.tooltipInfo.map((info) => ({
      District: selectedDistrictAttendance,
      Municipality: item.municipality,
      Name: info.name,
      Designation: info.designation,
      Attendance_Status: info.attendance_status,
      Check_In: info.check_in
        ? moment(info.check_in, "HH:mm:ss").format("h:mm A")
        : "N/A",
      Check_Out: info.check_out
        ? moment(info.check_out, "HH:mm:ss").format("h:mm A")
        : "N/A",
    }))
  );

 
//   // CSV data for defaulters
// const csvDefaultersData = filteredMunicipalityDefaultersData.flatMap((item) =>
//   item.tooltipInfo.map((info) => ({
//     District:
//       selectedDistrictDefaulters === "all"
//         ? item.district // Use district from API
//         : selectedDistrictDefaulters, // Use selected district if not "all"
//     Municipality: item.municipality,
//     Name: info.name,
//     Designation: info.designation,
//     Attendance_Status: info.attendance_status ?? "N/A",
//     Check_In: info.check_in
//       ? moment(info.check_in, "HH:mm:ss").format("h:mm A")
//       : "N/A",
//     Check_Out: info.check_out
//       ? moment(info.check_out, "HH:mm:ss").format("h:mm A")
//       : "N/A",
//   }))
// );

// CSV data for defaulters
const csvDefaultersData = filteredMunicipalityDefaultersData.flatMap((item) =>
  item.tooltipInfo.map((info) => ({
    District:
      selectedDistrictDefaulters === "all"
        ? item.district || "N/A" // ✅ now works because we stored district
        : selectedDistrictDefaulters || "N/A",
    Municipality: item.municipality || "N/A",
    Name: info.name || "N/A",
    Designation: info.designation || "N/A",
    Attendance_Status: info.attendance_status ?? "N/A",
    Check_In: info.check_in
      ? moment(info.check_in, "HH:mm:ss").format("h:mm A")
      : "N/A",
    Check_Out: info.check_out
      ? moment(info.check_out, "HH:mm:ss").format("h:mm A")
      : "N/A",
  }))
);




  // --- District Pie Chart Data ---
  const sortedDistricts = [...districtUsers].sort(
    (a, b) => b.total_users - a.total_users
  );
  const top10 = sortedDistricts.slice(0, 10);
  const others = sortedDistricts.slice(10);

  const districtData = [
    ...top10.map((item) => ({
      name: item.district,
      value: item.total_users,
    })),
    ...(others.length > 0
      ? [
          {
            name: "Others",
            value: others.reduce((sum, d) => sum + d.total_users, 0),
          },
        ]
      : []),
  ];

  return (
    <div
      className="min-h-screen p-4 md:p-8"
      style={{ backgroundColor: theme.colors.background }}
    >
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-8">
          Admin Dashboard
        </h1>

        {/* Stat Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} height={120} borderRadius={12} />
            ))
          ) : (
            <>
              <StatCard
                icon={<FiUsers className="text-3xl text-blue-500" />}
                label="Total Users"
                value={stats?.total_users || 0}
              />
              <StatCard
                icon={<FiClipboard className="text-3xl text-green-500" />}
                label="Total Attendance"
                value={stats?.total_attendance || 0}
              />
              <StatCard
                icon={<FiCalendar className="text-3xl text-purple-500" />}
                label="Total Leaves"
                value={stats?.total_leaves || 0}
              />
              <StatCard
                icon={<FiCheckCircle className="text-3xl text-yellow-500" />}
                label="Total Task Submit"
                value={stats?.total_task_submit || 0}
              />
            </>
          )}
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Pie Chart */}
          <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
            <h2 className="text-xl font-semibold mb-4 text-gray-800 flex items-center gap-2">
              <FiMapPin className="text-blue-500" />
              District-wise User Distribution
            </h2>

            {districtData.length > 0 ? (
              <div className="flex flex-col">
                <div className="h-[350px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={districtData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) =>
                          `${name} (${(percent * 100).toFixed(1)}%)`
                        }
                        outerRadius={120}
                        fill={theme.colors.primary}
                        dataKey="value"
                      >
                        {districtData.map((_, index) => (
                          <Cell
                            key={index}
                            fill={COLORS[index % COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                <div className="mt-4 max-h-[200px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2">District</th>
                        <th className="text-right py-2">Users</th>
                      </tr>
                    </thead>
                    <tbody>
                      {districtData
                        .filter((item) => item.name !== "Others")
                        .map((item, index) => (
                          <tr key={index} className="border-b">
                            <td className="py-2 flex items-center">
                              <span
                                className="inline-block w-3 h-3 mr-2 rounded-full"
                                style={{
                                  backgroundColor:
                                    COLORS[index % COLORS.length],
                                }}
                              ></span>
                              {item.name}
                            </td>
                            <td className="text-right py-2">{item.value}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <p className="text-gray-500">No data available.</p>
            )}
          </div>

          {/* District Task Submissions */}
          <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200 overflow-x-auto">
            <h2 className="text-xl font-semibold mb-4 text-gray-800 flex items-center gap-2">
              <FiClipboard className="text-green-500" /> District-wise Task
              Submissions
            </h2>

            {districtTaskSubmit?.length > 0 ? (
              <div className="min-w-[800px]">
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart
                    data={districtTaskSubmit}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="district"
                      angle={-45}
                      textAnchor="end"
                      interval={0}
                      height={120}
                    />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Legend />
                    <Bar
                      dataKey="total_users"
                      fill={theme.colors.primary}
                      name="Total Users"
                    />
                    <Bar
                      dataKey="total_task_submit"
                      fill={theme.colors.warning}
                      name="Total Task Submit"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="text-gray-500">No data available.</p>
            )}
          </div>
        </div>

        {/* Municipality-wise Attendance */}
        <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200 overflow-x-auto mt-10">
          <div className="flex items-center justify-between flex-wrap gap-4 mb-4">
            <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
              <FiClipboard className="text-blue-500" />
              Municipality-wise Attendance
            </h2>

            <div>
              <label className="mr-2 text-sm font-medium text-gray-700">
                Filter by District:
              </label>
              <select
                value={selectedDistrictAttendance}
                onChange={(e) => setSelectedDistrictAttendance(e.target.value)}
                style={dropdownStyle}
              >
                <option value="">Select District</option>
                {allDistrictsAttendance.map((district) => (
                  <option key={district} value={district}>
                    {district}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex gap-4 flex-wrap items-center">
              <div>
                <label className="text-sm font-medium text-gray-700 mr-2">
                  Select Date:
                </label>
                <DatePicker
                  selected={selectedDateAttendance}
                  onChange={(date) => setSelectedDateAttendance(date)}
                  dateFormat="yyyy-MM-dd"
                  className="border border-gray-300 rounded px-2 py-1 text-sm"
                />
              </div>

              {filteredMunicipalityData.length > 0 && (
                <CSVLink
                  data={csvAttendanceData}
                  filename={`Attendance_Report_${selectedDistrictAttendance}_${moment(
                    selectedDateAttendance
                  ).format("YYYY-MM-DD")}.csv`}
                  className="text-sm text-white bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded"
                >
                  Export CSV
                </CSVLink>
              )}
            </div>
          </div>

          {selectedDistrictAttendance === "" ? (
            <p className="text-gray-500 text-sm pl-2">
              Please select a district to view attendance.
            </p>
          ) : filteredMunicipalityData.length > 0 ? (
            <div className="min-w-[1000px]">
              <ResponsiveContainer width="100%" height={400}>
                <BarChart
                  data={filteredMunicipalityData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="municipality"
                    angle={-45}
                    textAnchor="end"
                    interval={0}
                    height={120}
                  />
                  <YAxis allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar
                    dataKey="total_users"
                    fill={theme.colors.primary}
                    name="Total Users"
                  />
                  <Bar
                    dataKey="total_attendance"
                    fill={theme.colors.secondary}
                    name="Total Attendance"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-gray-500">
              No data available for selected district.
            </p>
          )}
        </div>

        {/* Municipality-wise Attendance Defaulters */}
        <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200 overflow-x-auto mt-10">
          <div className="flex items-center justify-between flex-wrap gap-4 mb-4">
            <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
              <FiClipboard className="text-red-500" />
              Municipality-wise Attendance Defaulters
            </h2>

            <div>
              <label className="mr-2 text-sm font-medium text-gray-700">
                Filter by District:
              </label>
              <select
                value={selectedDistrictDefaulters}
                onChange={(e) => setSelectedDistrictDefaulters(e.target.value)}
                style={dropdownStyle}
              >
               <option value="">Select District</option>
<option value="all">All</option>
{allDistrictsDefaulters.map((district) => (
  <option key={district} value={district}>
    {district}
  </option>
))}

              </select>
            </div>

            <div className="flex gap-4 flex-wrap items-center">
              <div>
                <label className="text-sm font-medium text-gray-700 mr-2">
                  Select Date:
                </label>
                <DatePicker
                  selected={selectedDateDefaulters}
                  onChange={(date) => setSelectedDateDefaulters(date)}
                  dateFormat="yyyy-MM-dd"
                  className="border border-gray-300 rounded px-2 py-1 text-sm"
                />
              </div>

              {filteredMunicipalityDefaultersData.length > 0 && (
                <CSVLink
                  data={csvDefaultersData}
                  filename={`Defaulters_Report_${selectedDistrictDefaulters}_${moment(
                    selectedDateDefaulters
                  ).format("YYYY-MM-DD")}.csv`}
                  className="text-sm text-white bg-red-600 hover:bg-red-700 px-4 py-2 rounded"
                >
                  Export CSV
                </CSVLink>
              )}
            </div>
          </div>

          {selectedDistrictDefaulters === "" ? (
            <p className="text-gray-500 text-sm pl-2">
              Please select a district to view defaulters.
            </p>
          ) : filteredMunicipalityDefaultersData.length > 0 ? (
            <div className="min-w-[1000px]">
              <ResponsiveContainer width="100%" height={400}>
                <BarChart
                  data={filteredMunicipalityDefaultersData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="municipality"
                    angle={-45}
                    textAnchor="end"
                    interval={0}
                    height={120}
                  />
                  <YAxis allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar
                    dataKey="total_users"
                    fill={theme.colors.danger}
                    name="Total Users"
                  />
                  <Bar
                    dataKey="total_attendance"
                    fill={theme.colors.warning}
                    name="Total Defaulters"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-gray-500">
              No defaulters data available for selected district.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
